import { Controller } from "@hotwired/stimulus"

// Serielles Terminal des Config Tools, portiert aus dem alten DMCT. Den Port hält die Shell
// (window.configTool.serial): er bleibt über Turbo-Navigationen offen, dieser Controller spiegelt nur ihren Zustand.
// Beim Verbinden holt er den aktuellen Status, abonniert die gepushten Zeilen und zeigt sie an.
//
// Zeilen vom Gerät tragen die Tags des alten DMCT (<TI>, <SC>, <WN>, <ER>, <RQ>, <DP>, <CP>, <LF>); der Tag bestimmt
// die Farbe, der Rest wird als Text gezeigt. Die Reaktionen des Testablaufs (Ja/Nein-Dialoge, LoRa-Schlüssel-Anfragen)
// sind hier nicht portiert. Texte kommen als Values aus der View; %{path} und %{baud} werden hier ersetzt.
const MAX_LINES = 1000

const TAG_CLASSES = {
  TI: "fw-bold",
  SC: "text-success",
  WN: "text-warning",
  ER: "text-danger",
  RQ: "text-info",
  DP: "text-body-secondary",
  CP: "text-body-secondary",
}

export default class extends Controller {
  static targets = ["port", "baud", "connect", "disconnect", "unavailable", "output", "lineTemplate", "input", "sendButton", "status"]
  static values = { selectPort: String, connected: String, disconnected: String, error: String }

  #unsubscribe = []

  async connect() {
    if (!this.#bridge) {
      this.unavailableTarget.hidden = false
      return
    }
    this.#unsubscribe = [this.#bridge.serial.onLine((line) => this.#appendLine(line)), this.#bridge.serial.onStatus((status) => this.#renderStatus(status))]
    await this.reloadPorts()
    this.#renderStatus(await this.#bridge.serial.status())
  }

  disconnect() {
    this.#unsubscribe.forEach((off) => off())
    this.#unsubscribe = []
  }

  async reloadPorts() {
    const selected = this.portTarget.value
    const ports = await this.#bridge.serial.listPorts()
    this.portTarget.replaceChildren(
      ...ports.map((port) => {
        const option = document.createElement("option")
        option.value = port.path
        option.textContent = port.manufacturer ? `${port.path} — ${port.manufacturer}` : port.path
        return option
      }),
    )
    if (ports.some((p) => p.path === selected)) this.portTarget.value = selected
  }

  async open() {
    const path = this.portTarget.value
    if (!path) return this.#status(this.selectPortValue)
    try {
      await this.#bridge.serial.open({ path, baudRate: Number(this.baudTarget.value) })
    } catch (error) {
      this.#status(error.message)
    }
  }

  async close() {
    await this.#bridge.serial.close()
  }

  async send(event) {
    event.preventDefault()
    const text = this.inputTarget.value
    if (!text) return
    try {
      await this.#bridge.serial.write(`${text}\n`)
      this.#appendLine(`> ${text}`, "text-primary")
      this.inputTarget.value = ""
    } catch (error) {
      this.#status(error.message)
    }
  }

  clear() {
    this.outputTarget.replaceChildren()
  }

  #renderStatus({ open, path, baudRate, error }) {
    this.connectTarget.hidden = open
    this.disconnectTarget.hidden = !open
    this.portTarget.disabled = open
    this.baudTarget.disabled = open
    this.inputTarget.disabled = !open
    this.sendButtonTarget.disabled = !open
    if (open) this.portTarget.value = path
    this.#status(error ? `${this.errorValue} ${error}` : open ? this.connectedValue.replace("%{path}", path).replace("%{baud}", baudRate) : this.disconnectedValue)
  }

  #appendLine(raw, forcedClass = null) {
    const { text, className } = forcedClass ? { text: raw, className: forcedClass } : this.#parse(raw)
    const line = this.lineTemplateTarget.content.firstElementChild.cloneNode(true)
    line.textContent = text
    if (className) line.classList.add(className)

    const output = this.outputTarget
    const atBottom = output.scrollHeight - output.scrollTop - output.clientHeight < 8
    output.appendChild(line)
    while (output.childElementCount > MAX_LINES) output.firstElementChild.remove()
    if (atBottom) output.scrollTop = output.scrollHeight
  }

  // "<SC> alles gut\n" → { text: "alles gut", className: "text-success" }
  #parse(raw) {
    const line = raw.replace(/\r?\n$/, "")
    const match = line.match(/^<([A-Z]{2})>\s?(.*)$/)
    if (!match) return { text: line, className: null }
    const [, tag, rest] = match
    if (tag === "LF") return { text: "", className: null }
    return { text: rest, className: TAG_CLASSES[tag] ?? null }
  }

  #status(text) {
    this.statusTarget.textContent = text
  }

  get #bridge() {
    return window.configTool
  }
}
